/**
 * PDF repair — UI layer.
 *
 * Three outcomes, told apart plainly:
 *   HEALTHY   the cross-reference already points where it should; nothing done,
 *             and no run is billed for finding that out.
 *   REBUILT   a fresh index was written and verified against the objects it
 *             claims. The file downloads.
 *   TEXT ONLY the document cannot be made openable — but the words live in the
 *             content streams and come out anyway.
 */

import { examine, repair, extractText } from './pdf-repair.js';

const T = (k, p) => {
  let s = (window.__PR_I18N && window.__PR_I18N[k]) || k;
  if (p) for (const [a, b] of Object.entries(p)) s = s.replace(new RegExp(':' + a, 'g'), b);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

let current = null;

async function analyse(file) {
  const out = el('pr-out');
  out.innerHTML = `<div class="pr-working">${T('working')}</div>`;
  out.hidden = false;
  window.__stage && window.__stage('analyzing');

  let info;
  try {
    info = await examine(file);
  } catch (err) {
    const code = (err && err.code) || 'generic';
    out.innerHTML = card('bad', T('verdict_failed'), T('err_' + code));
    window.__ae && window.__ae('tool_blocked', { slug: 'pdf-repair', reason: code, size: file.size });
    return;
  }

  const damaged = !info.xrefOk || info.issues.some((i) => i.code !== 'encrypted');

  /* Diagnosis is free. Answering "your document was fine all along" must never
   * cost the day's run — the same rule office-repair follows. */
  const _run = window.__toolLimit && damaged && !info.encrypted
    ? window.__toolLimit.begin('pdf-repair',
        window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null)
    : null;
  if (window.__toolLimit && damaged && !info.encrypted && !_run) return;

  let result = null, failure = null;
  if (damaged && !info.encrypted) {
    try {
      result = await repair(file, { examined: info, name: file.name });
    } catch (err) {
      failure = (err && err.code) || 'generic';
    }
  }

  let text = '';
  try { text = await extractText(file); } catch (_) { /* salvage is best-effort */ }
  current = { file, info, result, text };

  out.innerHTML = [
    renderVerdict(info, result, failure, damaged),
    renderFacts(info),
    renderIssues(info),
    renderSalvage(text),
  ].join('');
  wire();

  if (_run) { if (result) _run.commit(); else _run.abort(); }
  window.__aeToolComplete && window.__aeToolComplete({
    damaged: damaged ? 1 : 0, repaired: result ? 1 : 0,
    encrypted: info.encrypted ? 1 : 0,
    objects: info.objects, pages: info.pages,
    issues: info.issues.length, text_chars: text.length,
  });
}

const card = (tone, title, body, extra = '') =>
  `<div class="pr-card pr-tone-${tone}"><p class="pr-title">${title}</p>
     <p class="pr-body">${body}</p>${extra}</div>`;

function renderVerdict(info, result, failure, damaged) {
  if (info.encrypted) {
    return card('warn', T('verdict_encrypted'), T('body_encrypted'));
  }
  if (!damaged) {
    return card('ok', T('verdict_healthy'), T('body_healthy', { pages: String(info.pages || '?') }));
  }
  if (!result) {
    return card('bad', T('verdict_failed'), T('err_' + (failure || 'generic')));
  }
  const r = result.report;
  return card('ok', T('verdict_repaired'),
    T('body_repaired', { objects: String(r.objects), added: human(r.addedBytes) }),
    `<p class="pr-note">${T('note_appended')}</p>
     <div class="pr-actions"><button type="button" class="pr-btn" id="pr-save">${T('save')}</button></div>`);
}

/** The numbers that tell the user whether recovery is worth attempting. */
function renderFacts(info) {
  const rows = [
    info.version ? [T('fact_version'), esc(info.version)] : null,
    [T('fact_objects'), String(info.objects)],
    info.pages ? [T('fact_pages'), String(info.pages)] : null,
    info.root ? [T('fact_root'), `${info.root.num} (${T('root_' + info.root.source)})`] : null,
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');
  return `<section class="pr-block"><h3>${T('facts_title')}</h3><table class="pr-facts">${rows}</table></section>`;
}

function renderIssues(info) {
  const real = info.issues.filter((i) => i.code !== 'encrypted');
  if (!real.length) return '';
  const items = real.map((i) =>
    `<li>${T('issue_' + i.code, { at: i.at != null ? String(i.at) : '', want: i.want != null ? String(i.want) : '' })}</li>`);
  return `<section class="pr-block"><h3>${T('issues_title')}</h3><ul class="pr-list">${items.join('')}</ul></section>`;
}

function renderSalvage(text) {
  if (!text) return '';
  return `<section class="pr-block"><h3>${T('salvage_title')}</h3>
      <p class="pr-body">${T('salvage_desc')}</p>
      <pre class="pr-text">${esc(text.slice(0, 4000))}${text.length > 4000 ? '\n…' : ''}</pre>
      <div class="pr-actions"><button type="button" class="pr-btn pr-btn-ghost" id="pr-save-text">${T('save_text')}</button></div>
    </section>`;
}

function wire() {
  const save = el('pr-save');
  if (save) save.addEventListener('click', () => {
    window.__downloadFile(current.result.blob, current.result.filename);
    window.__ae && window.__ae('feature_use', { slug: 'pdf-repair', feature: 'saved_pdf' });
  });
  const st = el('pr-save-text');
  if (st) st.addEventListener('click', () => {
    const base = (current.file.name || 'document').replace(/\.[^.]+$/, '');
    window.__downloadFile(new Blob([current.text], { type: 'text/plain;charset=utf-8' }), `${base}.txt`);
    window.__ae && window.__ae('feature_use', { slug: 'pdf-repair', feature: 'saved_text' });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('prDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (e) => {
    const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
    if (f) analyse(f);
  });
});

export { analyse };
